import { useState, useEffect, useRef } from 'react';
import { useBrainStore } from '@/state/brainStore';
import { useUIStore } from '@/state/uiStore';
import { useSimulationStore } from '@/state/simulationStore';
import { useNavigate } from 'react-router-dom';
import { startOutboundCall } from '@/domain/callFlow';
import { pushToQueue, recordContactAttempt } from '@/domain/sequenceFlow';
import {
  customerName,
  extractAuPhone,
  extractCallbackTime,
  simTimeShort,
} from '@/utils/format';
import type { CustomerAction } from '@/components/CustomerActionModals';
import DepositLinkModal from '@/components/DepositLinkModal';
import type { ConversationEntry, CustomerRecord } from '@/domain/types';
import {
  Send,
  Phone,
  Wallet,
  Hash,
  MoreHorizontal,
  MessageSquare,
  AtSign,
  Mail,
  ChevronDown,
  ScrollText,
  Sparkles,
  Calendar,
  XCircle,
  ListPlus,
  Check,
  ArrowRight,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';

type MessageChannel = 'sms' | 'meta-dm' | 'ig-dm' | 'email';

interface Props {
  customer: CustomerRecord;
  onOpenModal?: (action: CustomerAction) => void;
  initialChannel?: MessageChannel;
  /**
   * Prefills the composer body - used by the confirmations work modal to seed
   * a follow-up draft after a no-answer outcome. Re-applies whenever the value
   * changes (e.g. the operator picks "No answer" after the composer has
   * already mounted); does not clobber further edits once set, since the
   * dependency only changes when a fresh draft is supplied.
   */
  initialDraft?: string;
  /**
   * Where this composer is mounted. Drives which actions appear, via
   * ACTIONS_BY_HOST below - the composer is shared by four surfaces whose
   * relevant actions genuinely differ, and one named table beats threading a
   * boolean per button. Omitted means every action shows.
   */
  host?: ComposerHost;
}

/** The surfaces that mount this composer. */
export type ComposerHost = 'queue' | 'customer-record' | 'confirmations';

interface ComposerActions {
  deposit: boolean;
  selfBook: boolean;
  pushToQueue: boolean;
}

const ALL_ACTIONS: ComposerActions = { deposit: true, selfBook: true, pushToQueue: true };

/**
 * Which actions each host shows. Nothing is deleted - a host that turns an action
 * off just doesn't render it, and flipping one back on is a single edit here.
 *
 * Self-book is off on every agent surface: the artist taking the piece generates
 * that link, not the front desk.
 */
const ACTIONS_BY_HOST: Record<ComposerHost, ComposerActions> = {
  // My Queue work modal. The client is already in the agent's queue, so pushing
  // them to it again is meaningless (and the modal header already shows their
  // sequence stage). Deposits come off the availability pane, grounded to a
  // pinned slot.
  queue: { deposit: false, selfBook: false, pushToQueue: false },
  // Front desk customer record - which is where the Conversations inbox lands
  // when you open a thread. SlotDepositPane sits directly below this composer on
  // that screen and sends a deposit grounded to a picked slot, so the composer's
  // ungrounded duplicate goes the same way it did in the queue. Still the one
  // surface that hands a fresh lead to a booking agent.
  'customer-record': { deposit: false, selfBook: false, pushToQueue: true },
  // Confirmations work modal: working an existing booking, but a client who wants
  // to rebook still needs handing over.
  confirmations: { deposit: true, selfBook: false, pushToQueue: true },
};

const CHANNEL_OPTIONS: Array<{
  value: MessageChannel;
  label: string;
  Icon: typeof MessageSquare;
}> = [
  { value: 'sms', label: 'SMS', Icon: MessageSquare },
  { value: 'meta-dm', label: 'Meta DM', Icon: MessageSquare },
  { value: 'ig-dm', label: 'Instagram DM', Icon: AtSign },
  { value: 'email', label: 'Email', Icon: Mail },
];

const TEMPLATES = [
  { label: 'Deposit nudge', body: 'Hey {first}, just following up on the deposit link. Want me to extend the expiry?' },
  { label: 'Reschedule offer', body: 'Hi {first}, I can move your session. What day works for you?' },
  { label: 'Pre-session prep', body: 'Hey {first}, quick reminder for your session: eat before, stay hydrated, and bring photo ID.' },
  { label: 'Booking confirmation', body: 'You\'re booked, {first}. We\'ll send a reminder 24h before. Reply YES to confirm.' },
  { label: 'Reactivation tap', body: 'Hey {first}, long time no see. Saw your old design intent on file, want me to lock something in?' },
];

export default function CustomerComposer({
  customer,
  onOpenModal,
  initialChannel,
  initialDraft,
  host,
}: Props) {
  const actions = host ? ACTIONS_BY_HOST[host] : ALL_ACTIONS;
  const navigate = useNavigate();
  const addConvo = useBrainStore((s) => s.addConversationEntry);
  const appendActivity = useBrainStore((s) => s.appendActivity);
  const pushNotification = useUIStore((s) => s.pushNotification);
  const clock = useSimulationStore((s) => s.clockIso);
  const sequence = useBrainStore((s) => s.sequenceState[customer.id]);

  const inferredChannel: MessageChannel = (() => {
    const last = customer.conversation[customer.conversation.length - 1];
    if (
      last?.channel === 'meta-dm' ||
      last?.channel === 'ig-dm' ||
      last?.channel === 'sms' ||
      last?.channel === 'email'
    ) {
      return last.channel;
    }
    return 'sms';
  })();

  const [channel, setChannel] = useState<MessageChannel>(initialChannel ?? inferredChannel);
  const [body, setBody] = useState(initialDraft ?? '');
  const [channelOpen, setChannelOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [pushOpen, setPushOpen] = useState(false);
  const channelRef = useRef<HTMLDivElement>(null);
  const tmplRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLDivElement>(null);
  const pushRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (channelRef.current && !channelRef.current.contains(e.target as Node)) setChannelOpen(false);
      if (tmplRef.current && !tmplRef.current.contains(e.target as Node)) setTemplatesOpen(false);
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) setMoreOpen(false);
      if (pushRef.current && !pushRef.current.contains(e.target as Node)) setPushOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPushOpen(false);
    };
    window.addEventListener('mousedown', handler);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', handler);
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  // A fresh draft (e.g. the confirmations work modal revealing its no-answer
  // follow-up after the composer already mounted) re-seeds the body. Only
  // fires when the draft string itself changes, so it never clobbers an
  // operator's edits mid-typing.
  useEffect(() => {
    if (initialDraft) setBody(initialDraft);
  }, [initialDraft]);

  // "In queue · sequence N" label for the flipped button. Numeric stages read as
  // sequence 1/2/3; a red terminal state reads as "red".
  const inQueueLabel = sequence
    ? `In queue · ${sequence.stage === 'red' ? 'red' : `sequence ${sequence.stage}`}`
    : null;

  const channelSpec = CHANNEL_OPTIONS.find((c) => c.value === channel) ?? CHANNEL_OPTIONS[0]!;

  const onSend = () => {
    if (!body.trim()) return;
    const entry: ConversationEntry = {
      id: `msg-${Date.now()}`,
      customerId: customer.id,
      channel,
      direction: 'out',
      body,
      timestamp: clock,
      authorRole: 'agent',
      authorName: 'You',
    };
    addConvo(customer.id, entry);
    appendActivity({
      id: `act-msg-${entry.id}`,
      type: channel === 'sms' ? 'sms-sent' : 'meta-dm',
      customerId: customer.id,
      body: `${channelSpec.label} sent to ${customerName(customer)}.`,
      timestamp: clock,
    });
    pushNotification({
      intensity: 'info',
      title: `${channelSpec.label} sent`,
      body: `Delivered to ${customerName(customer)}.`,
      ttlMs: 3500,
    });
    setBody('');
    // A sent message counts as a contact attempt: it advances an existing
    // sequence, and from the queue it also STARTS one for a raw lead (a missed
    // call / fresh enquiry has no sequence yet), so contacting it makes it go
    // dormant and move to "Completed this sequence" (bd tv-jpx). Outside the
    // queue we keep the guard so casual record-page messages never mint a
    // sequence.
    if (sequence || host === 'queue') recordContactAttempt(customer.id);
  };

  const onCall = () => {
    startOutboundCall({ customerId: customer.id, studioId: customer.primaryStudioId });
  };

  const [depositModalOpen, setDepositModalOpen] = useState(false);

  return (
    <>
    <footer
      className="border-t border-gold-dim/20 bg-ink-deep sticky bottom-0"
      data-testid="customer-composer"
    >
        <div className="px-6 pt-3 pb-1 flex items-center gap-2 flex-wrap">
          {/* Channel selector */}
          <div ref={channelRef} className="relative">
            <button
              type="button"
              onClick={() => setChannelOpen((v) => !v)}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-gold-bright/50 text-gold-bright hover:bg-gold-bright/10 text-xs"
              data-testid="composer-channel"
            >
              <channelSpec.Icon size={12} /> {channelSpec.label}
              <ChevronDown size={10} />
            </button>
            {channelOpen ? (
              <div className="absolute bottom-full mb-2 left-0 z-30 panel py-1 min-w-[160px]">
                {CHANNEL_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      setChannel(opt.value);
                      setChannelOpen(false);
                    }}
                    className={clsx(
                      'flex w-full items-center gap-2 px-3 py-1.5 text-xs',
                      channel === opt.value ? 'text-gold-bright' : 'text-cream-soft hover:text-cream',
                    )}
                    data-testid={`composer-channel-${opt.value}`}
                  >
                    <opt.Icon size={12} /> {opt.label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          {/* Templates */}
          <div ref={tmplRef} className="relative">
            <button
              type="button"
              onClick={() => setTemplatesOpen((v) => !v)}
              className="btn-quiet text-[11px]"
              data-testid="composer-templates"
            >
              Templates <ChevronDown size={10} />
            </button>
            {templatesOpen ? (
              <div className="absolute bottom-full mb-2 left-0 z-30 panel py-1 min-w-[240px]">
                {TEMPLATES.map((t) => (
                  <button
                    key={t.label}
                    type="button"
                    onClick={() => {
                      setBody(t.body.replace('{first}', customer.firstName));
                      setTemplatesOpen(false);
                    }}
                    className="block w-full text-left px-3 py-2 text-xs text-cream-soft hover:text-cream hover:bg-ink-soft/40"
                  >
                    <span className="block font-inter text-cream">{t.label}</span>
                    <span className="block font-serif text-[10px] text-cream-soft/70 mt-0.5 truncate">
                      {t.body.replace('{first}', customer.firstName).slice(0, 60)}…
                    </span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <span className="h-4 w-px bg-gold-dim/40 mx-1" />

          {/* Inline action icons */}
          <button
            type="button"
            onClick={onCall}
            className="btn-ghost text-[11px]"
            data-testid="composer-call"
            title="Start outbound call"
          >
            <Phone size={12} /> Call
          </button>
          {actions.deposit ? (
            <button
              type="button"
              onClick={() => setDepositModalOpen(true)}
              className="btn-ghost text-[11px]"
              data-testid="composer-deposit"
              title="Send Stripe deposit link"
            >
              <Wallet size={12} /> Deposit link
            </button>
          ) : null}
          {actions.selfBook ? (
            <button
              type="button"
              onClick={() =>
                navigate('/self-book-generator', { state: { customerId: customer.id } })
              }
              className="btn-ghost text-[11px]"
              data-testid="composer-self-book"
              title="Generate a self-book link"
            >
              <Hash size={12} /> Self-book
            </button>
          ) : null}

          {/* Push to queue — hands the conversation to a booking agent, so it is
              separated from the message-send actions and pushed to the right.
              Hidden in the queue modal: the agent working it IS the booking agent
              it would hand to. */}
          {actions.pushToQueue ? (
          <div ref={pushRef} className="relative ml-auto flex items-center gap-2">
            <span className="h-4 w-px bg-gold-dim/40" />
            {inQueueLabel ? (
              <button
                type="button"
                onClick={() => setPushOpen((v) => !v)}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-mint/50 text-mint text-[11px]"
                data-testid="composer-in-queue"
                title="Already handed to a booking agent"
              >
                <Check size={12} /> {inQueueLabel}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setPushOpen((v) => !v)}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-gold-bright/55 bg-gold/10 text-gold-bright text-[11px] uppercase tracking-wider"
                data-testid="composer-push"
                title="Hand this lead to a booking agent"
              >
                <ListPlus size={12} /> Push to queue
              </button>
            )}
            {pushOpen ? (
              sequence ? (
                <PushDuplicate
                  label={inQueueLabel ?? 'In queue'}
                  onJump={() => {
                    setPushOpen(false);
                    navigate('/queue');
                  }}
                />
              ) : (
                <PushForm
                  customer={customer}
                  clock={clock}
                  onCancel={() => setPushOpen(false)}
                  onConfirm={(payload) => {
                    pushToQueue(payload);
                    setPushOpen(false);
                    pushNotification({
                      intensity: 'info',
                      title: 'Pushed to queue',
                      body: `${customerName(customer)} handed to a booking agent.`,
                      ttlMs: 3500,
                    });
                  }}
                />
              )
            ) : null}
          </div>
          ) : null}

          {/* More menu. Pushed right on its own when the queue cluster is hidden,
              since that cluster was carrying the ml-auto. */}
          <div ref={moreRef} className={clsx('relative', !actions.pushToQueue && 'ml-auto')}>
            <button
              type="button"
              onClick={() => setMoreOpen((v) => !v)}
              className="btn-quiet text-[11px]"
              data-testid="composer-more"
              title="More actions"
            >
              <MoreHorizontal size={14} />
            </button>
            <AnimatePresence>
              {moreOpen ? (
                <motion.div
                  initial={{ y: 4, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: 4, opacity: 0 }}
                  className="absolute bottom-full mb-2 right-0 z-30 panel py-1 min-w-[200px]"
                >
                  <MoreItem
                    Icon={ScrollText}
                    label="Rapport note"
                    testid="composer-more-rapport"
                    onClick={() => {
                      onOpenModal?.('rapport');
                      setMoreOpen(false);
                    }}
                  />
                  <MoreItem
                    Icon={Calendar}
                    label="Tag future-readiness"
                    testid="composer-more-future"
                    onClick={() => {
                      onOpenModal?.('future');
                      setMoreOpen(false);
                    }}
                  />
                  <MoreItem
                    Icon={Sparkles}
                    label="Hand off to artist"
                    testid="composer-more-handoff"
                    onClick={() => {
                      onOpenModal?.('handoff');
                      setMoreOpen(false);
                    }}
                  />
                  <div className="h-px my-1 bg-gold-dim/30" />
                  <MoreItem
                    Icon={XCircle}
                    label="Cancel booking"
                    testid="composer-more-cancel"
                    onClick={() => {
                      onOpenModal?.('cancel');
                      setMoreOpen(false);
                    }}
                    blood
                  />
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
        </div>

        <div className="px-6 pb-4 pt-2 flex items-end gap-3">
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={`Reply to ${customer.firstName} on ${channelSpec.label}…`}
            rows={2}
            className="flex-1 min-w-0 bg-ink-black border border-gold-dim/30 rounded-lg px-3 py-2 text-sm text-cream font-serif resize-none focus:outline-none focus:border-gold-bright"
            data-testid="composer-body"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                onSend();
              }
            }}
          />
          <button
            type="button"
            onClick={onSend}
            disabled={!body.trim()}
            className="btn-primary text-xs"
            data-testid="composer-send"
          >
            <Send size={12} /> Send
          </button>
        </div>
    </footer>
    <DepositLinkModal
      open={depositModalOpen}
      customer={customer}
      onClose={() => setDepositModalOpen(false)}
    />
    </>
  );
}

// The confirm popover for a fresh push. Pre-fills phone (plain AU regex over the
// thread, editable, may be blank) and a callback time (if "call me at 3pm" appears).
// Description is an editable one-line captured note that travels on the queue item.
function PushForm({
  customer,
  clock,
  onCancel,
  onConfirm,
}: {
  customer: CustomerRecord;
  clock: string;
  onCancel: () => void;
  onConfirm: (payload: {
    customerId: string;
    description: string;
    phone?: string;
    pinnedCallbackAt?: string;
  }) => void;
}) {
  const threadText = customer.conversation.map((e) => e.body).join('  ');
  const inboundText = customer.conversation
    .filter((e) => e.direction === 'in')
    .map((e) => e.body)
    .join('  ');
  const detectedPhone = extractAuPhone(threadText);
  const detectedCallbackIso = extractCallbackTime(inboundText, clock);

  // Native date/time inputs work in local time, so derive their values from the
  // local parts of the sim clock and rebuild the ISO the same way.
  const toDateInput = (iso: string): string => {
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  const [phoneVal, setPhoneVal] = useState(detectedPhone ?? customer.phone ?? '');
  const [description, setDescription] = useState('');
  // Callback is empty by default so a lead with no callback pushes straight to the
  // queue. Both fields prefill only when a "call me at 3pm"-style time was detected.
  // Setting a time is what creates the callback; a lone date does nothing.
  const [callbackDate, setCallbackDate] = useState(
    detectedCallbackIso ? toDateInput(detectedCallbackIso) : '',
  );
  const [callbackTime, setCallbackTime] = useState(
    detectedCallbackIso ? simTimeShort(detectedCallbackIso) : '',
  );

  const combineToIso = (dateStr: string, timeStr: string): string | undefined => {
    const [y, mo, da] = dateStr.split('-').map((n) => Number.parseInt(n, 10));
    const [h, mi] = timeStr.split(':').map((n) => Number.parseInt(n, 10));
    if (y == null || mo == null || da == null || h == null || Number.isNaN(h)) return undefined;
    const when = new Date(y, mo - 1, da, h, Number.isNaN(mi ?? NaN) ? 0 : (mi ?? 0), 0, 0);
    return when.toISOString();
  };

  const submit = () =>
    onConfirm({
      customerId: customer.id,
      description,
      phone: phoneVal.trim() || undefined,
      // A time is required for a callback; if no date was picked, assume the sim day.
      pinnedCallbackAt: callbackTime
        ? combineToIso(callbackDate || toDateInput(clock), callbackTime)
        : undefined,
    });

  const fieldLabel = 'block text-[10px] uppercase tracking-widest text-cream-soft font-inter mb-1';
  const input =
    'w-full bg-ink-black border border-gold-dim/30 rounded-md px-2.5 py-1.5 text-sm text-cream font-serif focus:outline-none focus:border-gold-bright';

  return (
    <div
      className="absolute bottom-full mb-2 right-0 z-40 panel p-4 w-[300px] shadow-ink-soft"
      data-testid="composer-push-popover"
    >
      <p className="font-inter text-[11px] uppercase tracking-widest text-gold-bright">
        Push to queue
      </p>
      <p className="font-serif text-xs text-cream-soft mt-1 mb-3">
        Hands this lead to a booking agent. They pick it up in MyQ.
      </p>

      <div className="mb-3">
        <span className={fieldLabel}>Name</span>
        <input className={`${input} text-cream-soft`} value={customerName(customer)} readOnly />
      </div>

      <div className="mb-3">
        <div className="flex items-baseline justify-between">
          <span className={fieldLabel}>Phone</span>
          <span className="text-[9px] uppercase tracking-widest text-cream-faint font-inter">optional</span>
        </div>
        <input
          className={input}
          value={phoneVal}
          onChange={(e) => setPhoneVal(e.target.value)}
          placeholder="No number on file"
          data-testid="composer-push-phone"
        />
        {detectedPhone ? (
          <p className="mt-1 flex items-center gap-1.5 text-[10px] text-mint font-inter">
            <Check size={11} /> Found in message
          </p>
        ) : null}
      </div>

      <div className="mb-3">
        <span className={fieldLabel}>Description</span>
        <input
          className={input}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="e.g. wants a half-sleeve, quoted ~$1.2k"
          data-testid="composer-push-description"
        />
      </div>

      <div className="mb-1">
        <div className="flex items-baseline justify-between">
          <span className={fieldLabel}>Callback</span>
          <span className="text-[9px] uppercase tracking-widest text-cream-faint font-inter">optional</span>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="date"
            className={`${input} flex-1`}
            value={callbackDate}
            onChange={(e) => setCallbackDate(e.target.value)}
            data-testid="composer-push-callback-date"
          />
          <input
            type="time"
            className={`${input} w-[110px]`}
            value={callbackTime}
            onChange={(e) => setCallbackTime(e.target.value)}
            data-testid="composer-push-callback"
          />
        </div>
        {detectedCallbackIso ? (
          <p className="mt-1 flex items-center gap-1.5 text-[10px] text-mint font-inter">
            <Check size={11} /> Detected a callback time in the thread
          </p>
        ) : null}
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <button type="button" onClick={onCancel} className="btn-quiet text-[11px]">
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          className="btn-primary text-xs"
          data-testid="composer-push-confirm"
        >
          Push <ArrowRight size={12} />
        </button>
      </div>
    </div>
  );
}

// Shown when a sequence record already exists: no second push, just a jump link.
function PushDuplicate({ label, onJump }: { label: string; onJump: () => void }) {
  return (
    <div
      className="absolute bottom-full mb-2 right-0 z-40 panel p-4 w-[260px] shadow-ink-soft"
      data-testid="composer-push-duplicate"
    >
      <p className="font-inter text-sm text-gold-bright mb-1">
        {label.replace('In queue', 'Already in queue')}
      </p>
      <p className="font-serif text-xs text-cream-soft mb-3">A booking agent owns this lead.</p>
      <button
        type="button"
        onClick={onJump}
        className="btn-ghost text-[11px]"
        data-testid="composer-push-jump"
      >
        Jump to MyQ <ArrowRight size={12} />
      </button>
    </div>
  );
}

function MoreItem({
  Icon,
  label,
  onClick,
  testid,
  blood,
}: {
  Icon: typeof ScrollText;
  label: string;
  onClick: () => void;
  testid: string;
  blood?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={clsx(
        'flex w-full items-center gap-2 px-3 py-2 text-xs hover:bg-ink-soft/40 transition',
        blood ? 'text-blood' : 'text-cream-soft hover:text-cream',
      )}
    >
      <Icon size={12} /> {label}
    </button>
  );
}
